import React, { useState, useEffect } from 'react';
import { loadAllClasses, getSubclassesForClass } from '../../utils/classDataLoader';
import { EnhancedClass } from '../../types/class-data';
import { createDefaultBrawlerData } from '../../types/character';
import Card from '../Common/Card';
import './DeveloperPage.css';

// Import the standardized class features display component
import ClassFeaturesDisplay from '../CharacterSheet/ClassFeaturesDisplay';
import ClassSectionDiagnostic from './ClassSectionDiagnostic';

interface ClassSubclassCombination {
  class: EnhancedClass;
  subclass: any;
  key: string;
}

const DeveloperPage: React.FC = () => {
  const [classes, setClasses] = useState<EnhancedClass[]>([]);
  const [combinations, setCombinations] = useState<ClassSubclassCombination[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<'classes' | 'combinations' | 'diagnostics'>('classes');

  useEffect(() => {
    const loadClassData = async () => {
      try {
        setLoading(true);
        const allClasses = await loadAllClasses();
        setClasses(allClasses);

        // Generate all class-subclass combinations
        const allCombinations: ClassSubclassCombination[] = [];
        
        for (const classData of allClasses) {
          const subclasses = await getSubclassesForClass(classData.id);
          
          if (subclasses.length > 0) {
            // Add combinations for each subclass
            for (const subclass of subclasses) {
              allCombinations.push({
                class: classData,
                subclass: subclass,
                key: `${classData.id}-${subclass.id}`
              });
            }
          } else {
            // Add class without subclass if no subclasses available
            allCombinations.push({
              class: classData,
              subclass: null,
              key: `${classData.id}-none`
            });
          }
        }

        setCombinations(allCombinations);
        setLoading(false);
      } catch (err) {
        setError('Failed to load class data');
        setLoading(false);
        console.error('Error loading class data:', err);
      }
    };

    loadClassData();
  }, []);

  // Create mock enhanced character for testing
  const createMockCharacter = (classData: EnhancedClass, subclass: any) => {
    const mockCharacter = {
      id: 'mock-character',
      name: `Mock ${classData.name}`,
      class: classData.id,
      subclass: subclass?.id || '',
      level: '3',
      tier: 2,
      heritage: 'Mock Heritage',
      traits: classData.suggestedTraits || {
        agility: 0, strength: 0, finesse: 0,
        instinct: 0, presence: 0, knowledge: 0
      },
      currentHope: 2,
      maxHitPoints: classData.startingHitPoints,
      maxStress: classData.startingStress,
      hpMarked: 0,
      stressMarked: 0,
      armorMarked: 0,
      baseEvasion: classData.startingEvasion,
      baseStats: {
        traits: classData.suggestedTraits || {
          agility: 0, strength: 0, finesse: 0,
          instinct: 0, presence: 0, knowledge: 0
        },
        resources: {
          hope: 0,
          hpSlots: classData.startingHitPoints,
          stressSlots: classData.startingStress,
          armor: 0,
          evasion: classData.startingEvasion,
          thresholds: {
            major: 8,
            severe: 15
          }
        },
        proficiency: 0
      },
      currentState: {
        hpMarked: 0,
        stressMarked: 0,
        armorMarked: 0,
        currentHope: 2
      },
      modifiers: [],
      equipment: [],
      activeSpells: [],
      activeClassFeatures: [],
      classSpecificData: classData.id === 'brawler' ? {
        brawler: createDefaultBrawlerData()
      } : {}
    };

    return {
      character: mockCharacter,
      computedStats: {
        traits: classData.suggestedTraits || {
          agility: 0, strength: 0, finesse: 0,
          instinct: 0, presence: 0, knowledge: 0
        },
        evasion: classData.startingEvasion,
        armor: 0,
        hpSlots: classData.startingHitPoints,
        stressSlots: classData.startingStress,
        hope: 5,
        thresholds: {
          major: 8,
          severe: 15
        }
      }
    };
  };

  const mockUpdateCharacterData = () => {
    // Mock function for class features that need it
  };

  // Use the standardized ClassFeaturesDisplay directly (it's self-contained with CollapsibleSection)
  const getClassFeatureComponent = (classId: string, enhancedCharacter: any) => {
    const props = { 
      enhancedCharacter, 
      updateCharacterData: mockUpdateCharacterData 
    };

    return <ClassFeaturesDisplay {...props} />;
  };

  if (loading) {
    return (
      <div className="developer-page">
        <div className="developer-page__loading">
          Loading class data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="developer-page">
        <div className="developer-page__error">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="developer-page">
      <header className="developer-page__header">
        <h1>🛠️ Developer Visual Verification</h1>
        <p>Class features showcase for UI consistency verification</p>
        
        <div className="developer-page__nav">
          <button
            className={`nav-button ${selectedView === 'classes' ? 'active' : ''}`}
            onClick={() => setSelectedView('classes')}
          >
            Class Features ({classes.length})
          </button>
          <button
            className={`nav-button ${selectedView === 'combinations' ? 'active' : ''}`}
            onClick={() => setSelectedView('combinations')}
          >
            All Combinations ({combinations.length})
          </button>
          <button
            className={`nav-button ${selectedView === 'diagnostics' ? 'active' : ''}`}
            onClick={() => setSelectedView('diagnostics')}
          >
            Class Registry Diagnostics
          </button>
        </div>
      </header>

      <main className="developer-page__content">
        {selectedView === 'classes' && (
          <section className="developer-page__section">
            <h2>All Class Features</h2>
            <div className="class-features-grid">
              {classes.map((classData) => {
                const mockCharacter = createMockCharacter(classData, null);
                return (
                  <div key={classData.id} className="class-feature-card">
                    <div className="class-feature-header">
                      <h3>{classData.name}</h3>
                      <span className="class-domains">
                        Domains: {classData.domains.join(', ')}
                      </span>
                    </div>
                    <div className="class-feature-content">
                      {getClassFeatureComponent(classData.id, mockCharacter)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {selectedView === 'combinations' && (
          <section className="developer-page__section">
            <h2>All Class × Subclass Combinations</h2>
            <div className="combinations-grid">
              {combinations.map((combo) => {
                const mockCharacter = createMockCharacter(combo.class, combo.subclass);
                return (
                  <div key={combo.key} className="combination-card">
                    <div className="combination-header">
                      <h3>{combo.class.name}</h3>
                      {combo.subclass && (
                        <span className="subclass-name">
                          {combo.subclass.name}
                        </span>
                      )}
                      <span className="class-domains">
                        Domains: {combo.class.domains.join(', ')}
                      </span>
                    </div>
                    <div className="combination-content">
                      {getClassFeatureComponent(combo.class.id, mockCharacter)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {selectedView === 'diagnostics' && (
          <section className="developer-page__section">
            <ClassSectionDiagnostic />
          </section>
        )}
      </main>

      <footer className="developer-page__footer">
        <p>
          🔧 Development Mode Only • 
          Classes: {classes.length} • 
          Combinations: {combinations.length}
        </p>
      </footer>
    </div>
  );
};

export default DeveloperPage;